import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BGrainOverlay, BStripes, BFixedLogo, BHeader } from '../components/layout';
import { BSearchInput, BSuggestionChips, BCrateCard } from '../components/search';
import { getAuthHeaders } from '../../utils/odotId';
import './BSearchPage.css';

interface SearchHistoryEntry {
  reportId: string;
  queryDisplay: string;
  title?: string;
  genreTags?: string[];
  genreDescription?: string;
  searchedAt: string;
}

interface ConversationTurn {
  role: 'user' | 'agent';
  content: string;
}

const DEFAULT_SUGGESTIONS = [
  'brooklyn post-punk',
  'dreamy but heavy',
  '90s trip hop',
  'dark shoegaze',
];

export function BSearchPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [communitySearches, setCommunitySearches] = useState<SearchHistoryEntry[]>([]);
  const [userHistory, setUserHistory] = useState<SearchHistoryEntry[]>([]);
  const [filterTag, setFilterTag] = useState<string | null>(null);
  const [conversation, setConversation] = useState<ConversationTurn[]>([]);
  const conversationRef = useRef<HTMLDivElement>(null);

  const isInConversation = conversation.length > 0;

  // Fetch community searches (The Crates) and user history
  useEffect(() => {
    const fetchCommunity = async () => {
      try {
        const res = await fetch('/api/v1/research/community', {
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setCommunitySearches(data.searches || []);
        }
      } catch (err) {
        console.error('Failed to fetch community searches:', err);
      }
    };

    const fetchHistory = async () => {
      try {
        const res = await fetch('/api/v1/research/history', {
          headers: getAuthHeaders(),
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setUserHistory(data.searches || []);
        }
      } catch (err) {
        console.error('Failed to fetch user history:', err);
      }
    };

    fetchCommunity();
    fetchHistory();
  }, []);

  const handleSearch = async (searchQuery: string) => {
    setIsLoading(true);

    try {
      // Call the query endpoint with conversation history
      const res = await fetch('/api/v1/query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({
          input: searchQuery,
          history: conversation,
        }),
      });

      if (!res.ok) {
        throw new Error('Query failed');
      }

      const result = await res.json();

      if (result.status === 'ready') {
        // Query is ready - navigate to research page
        sessionStorage.setItem('b_query_result', JSON.stringify(result));
        sessionStorage.setItem('b_original_query', searchQuery);
        navigate('/research');
      } else if (result.status === 'question') {
        // AI needs more info - show conversation
        setConversation(result.conversation);
        setQuery('');
        setIsLoading(false);
        // Scroll to conversation
        setTimeout(() => {
          conversationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    } catch (err) {
      console.error('Search failed:', err);
      setConversation(prev => [
        ...prev,
        { role: 'user', content: searchQuery },
        { role: 'agent', content: "Something went wrong. Try again?" }
      ]);
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setConversation([]);
    setQuery('');
    setIsLoading(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
  };

  const handleTagClick = (tag: string) => {
    if (filterTag === tag) {
      setFilterTag(null); // Toggle off
    } else {
      setFilterTag(tag);
    }
  };

  // Community searches for the crates grid (filter out incomplete reports)
  const allCrateItems = communitySearches
    .filter(entry => entry.title && entry.title !== 'Discovering artists...');

  // Apply tag filter if active
  const crateItems = filterTag
    ? allCrateItems.filter(entry => entry.genreTags?.includes(filterTag)).slice(0, 50)
    : allCrateItems.slice(0, 50);


  // Determine card size based on grid position (0-indexed)
  // Pattern repeats every 13 cards: large at 0,4 | small at 8,9,10 | medium elsewhere
  const getCardSize = (index: number): 'small' | 'medium' | 'large' => {
    const posInPattern = index % 13;
    if (posInPattern === 0 || posInPattern === 4) return 'large';
    if (posInPattern >= 8 && posInPattern <= 10) return 'small';
    return 'medium';
  };

  return (
    <div className="b-page b-page--dark b-search-page">
      <BStripes />
      <BGrainOverlay />
      <BHeader showBack={false} showBookmark={false} showShare={false} />

      {/* Opening Section */}
      <section className="b-opening">
        <div className="b-opening__top-bar">
          <div className="b-opening__top-right">
            AI-Powered<br />Music Discovery
          </div>
        </div>

        {!isInConversation && <BFixedLogo />}
        <div className="b-opening__statement">
          {!isInConversation && (
            <h1 className="b-opening__headline">
              <div className="b-opening__line">
                <span className="b-opening__text">Discover</span>
              </div>
              <div className="b-opening__line b-opening__line--serif">
                <span className="b-opening__text">new</span>
              </div>
              <div className="b-opening__line">
                <span className="b-opening__text">Music</span>
              </div>
            </h1>
          )}

          {/* Conversation UI */}
          {isInConversation && (
            <div className="b-conversation" ref={conversationRef}>
              <button className="b-conversation__reset" onClick={handleReset}>
                Start over
              </button>
              {conversation.map((turn, i) => (
                <div key={i} className={`b-conversation__message b-conversation__message--${turn.role}`}>
                  {turn.content}
                  {turn.role === 'agent' && i === conversation.length - 1 && (
                    <span className="b-conversation__hint"> Or something else?</span>
                  )}
                </div>
              ))}
              {isLoading && (
                <div className="b-conversation__message b-conversation__message--agent b-conversation__thinking">
                  <span>.</span><span>.</span><span>.</span>
                </div>
              )}
            </div>
          )}

          <div className="b-opening__search">
            <BSearchInput
              onSubmit={handleSearch}
              isLoading={isLoading}
              value={query}
              onChange={setQuery}
              placeholder={isInConversation ? "Type your answer..." : undefined}
              autoFocus={isInConversation}
            />
            {!isInConversation && (
              <BSuggestionChips
                suggestions={DEFAULT_SUGGESTIONS}
                onSelect={handleSuggestionClick}
              />
            )}
            {!isInConversation && userHistory.filter(e => e.title && e.title !== 'Discovering artists...').length > 0 && (
              <button
                className="b-opening__my-stuff-link"
                onClick={() => navigate('/bookmarks')}
              >
                My Stuff
              </button>
            )}
          </div>
        </div>

        <div className="b-opening__bottom-bar">
          <div className="b-opening__scroll-hint">
            <span className="b-opening__scroll-line" />
            Scroll to explore
          </div>
          <div>Est. 2026</div>
        </div>
      </section>

      {/* Two-column section: My Stuff | The Crates */}
      {(allCrateItems.length > 0 || userHistory.length > 0) && (
        <section className="b-crates-section">
          <div className="b-crates-section__header">
            <h2 className="b-crates-section__title">Discover</h2>
            <span className="b-crates-section__subtitle">What's being explored</span>
          </div>

          {/* Tag filter bar - only show when filter is active */}
          {filterTag && (
            <div className="b-crates-section__filters">
              <button
                className="b-filter-tag b-filter-tag--active"
                onClick={() => setFilterTag(null)}
              >
                {filterTag} ✕
              </button>
            </div>
          )}

          <div className="b-crates-grid">
            {crateItems.map((item, index) => (
              <BCrateCard
                key={item.reportId}
                reportId={item.reportId}
                title={item.title || item.queryDisplay}
                description={item.genreDescription}
                genreTags={item.genreTags}
                number={index + 1}
                size={getCardSize(index)}
                onTagClick={handleTagClick}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
